import { computed } from 'vue'
import { useAuthPermissionsStore } from '@/stores/auth-permissions.store'
import type { AdminPermission } from '@/services/administrator-permissions'

/**
 * Composable for reactive permission checking in components
 *
 * @example
 * // In a component
 * const { can, canAny, canAll, isSuperAdmin } = usePermissions()
 *
 * const canCreateMerchant = can(AdminPermission.MerchantsCreate)
 * const canEditOrDelete = canAny([AdminPermission.MerchantsEdit, AdminPermission.MerchantsDelete])
 */
export function usePermissions() {
  const permissionsStore = useAuthPermissionsStore()

  /**
   * Check if user has a specific permission
   * Returns a reactive computed boolean
   */
  const can = (permission: AdminPermission) => {
    return computed(() => permissionsStore.hasPermission(permission))
  }

  /**
   * Check if user has ANY of the specified permissions
   * Returns a reactive computed boolean
   */
  const canAny = (permissionList: AdminPermission[]) => {
    return computed(() => permissionsStore.hasAnyPermission(permissionList))
  }

  /**
   * Check if user has ALL of the specified permissions
   * Returns a reactive computed boolean
   */
  const canAll = (permissionList: AdminPermission[]) => {
    return computed(() => permissionsStore.hasAllPermissions(permissionList))
  }

  /**
   * Direct access to isSuperAdmin flag
   */
  const isSuperAdmin = computed(() => permissionsStore.isSuperAdmin)

  /**
   * Check if permissions are loaded
   */
  const isLoaded = computed(() => permissionsStore.isLoaded)

  /**
   * Get all permissions
   */
  const allPermissions = computed(() => permissionsStore.permissions)

  return {
    can,
    canAny,
    canAll,
    isSuperAdmin,
    isLoaded,
    allPermissions
  }
}
